import { useState, useEffect } from 'react';
import { createIssue, uploadFile, queryCollection } from '../lib/api';
import { 
  PaperPlaneTilt, 
  Camera, 
  X, 
  CheckCircle, 
  WarningCircle,
  Spinner,
  House,
  Copy,
  MagnifyingGlass
} from '@phosphor-icons/react';

// Generate tracking number
const generateTrackingNumber = () => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `ARN-${year}-${random}`;
};

// Category type
interface Category {
  id: string;
  name: string;
  icon: string;
}

// Standalone external issue form - can be embedded in iframes
export default function ExternalIssueForm() {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    urgency: 3,
    reporter_name: '',
    reporter_email: '',
    reporter_phone: '',
    apartment_number: ''
  });
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [copied, setCopied] = useState(false);

  // Get organization ID from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orgId = params.get('org');
    const orgName = params.get('name');
    
    if (orgId) {
      setOrganizationId(orgId);
      // Fetch organization details and categories
      fetchOrganizationData(orgId);
    }
    if (orgName) {
      setOrganizationName(decodeURIComponent(orgName));
    }
  }, []);

  const fetchOrganizationData = async (orgId: string) => {
    try {
      // Fetch issue categories for this organization
      const catResult = await queryCollection<Category>('issue_categories', { 
        organization_id: orgId, 
        is_active: true 
      });
      if (catResult.length > 0) {
        setCategories(catResult);
      } else {
        // Default categories
        setCategories([
          { id: 'water', name: 'Vatten/Avlopp', icon: '💧' },
          { id: 'electrical', name: 'El/Belysning', icon: '💡' },
          { id: 'heating', name: 'Värme/Ventilation', icon: '🌡️' },
          { id: 'doors', name: 'Dörrar/Lås', icon: '🚪' },
          { id: 'outdoor', name: 'Utemiljö', icon: '🌳' },
          { id: 'elevator', name: 'Hiss', icon: '🛗' },
          { id: 'other', name: 'Övrigt', icon: '📋' }
        ]);
      }
    } catch (e) {
      console.error('Failed to fetch organization data:', e);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = e.target.files ? Array.from(e.target.files) : [];
    setFiles(prev => [...prev, ...newFiles].slice(0, 5)); // Max 5 files
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.reporter_name || !formData.reporter_email) {
      setError('Fyll i alla obligatoriska fält');
      return;
    }
    
    if (!organizationId) {
      setError('Organisationen kunde inte identifieras');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Upload files if any
      let fileUrls: Array<{name: string; url: string}> = [];
      for (const file of files) {
        try {
          const result = await uploadFile(file);
          fileUrls.push({ name: file.name, url: result.url || result.file_url || '' });
        } catch (e) {
          console.error('File upload failed:', e);
        }
      }
      
      // Generate tracking number
      const newTrackingNumber = generateTrackingNumber();
      
      // Create the issue
      const issueData = {
        ...formData,
        organization_id: organizationId,
        tracking_number: newTrackingNumber,
        status: 'open' as const,
        source: 'external_form',
        attachments: fileUrls.map(f => f.url),
        created_at: new Date().toISOString()
      };
      
      await createIssue(issueData);
      setTrackingNumber(newTrackingNumber);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit issue:', err);
      setError('Kunde inte skicka ärendet. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewReport = () => {
    setFormData({
      title: '',
      description: '',
      category: 'other',
      urgency: 3,
      reporter_name: '',
      reporter_email: '',
      reporter_phone: '',
      apartment_number: ''
    });
    setFiles([]);
    setSubmitted(false);
    setTrackingNumber(null);
    setError(null);
    setCopied(false);
  };

  const copyTrackingNumber = () => {
    if (trackingNumber) {
      navigator.clipboard.writeText(trackingNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Tack för din rapport!</h1>
          <p className="text-slate-600 mb-4">
            Ditt ärende har mottagits och kommer att behandlas så snart som möjligt.
          </p>
          
          {/* Tracking Number */}
          {trackingNumber && (
            <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-sm text-slate-500 mb-2">Ditt ärendenummer</p>
              <div className="flex items-center justify-center gap-2">
                <span className="font-mono text-xl font-bold text-slate-900">
                  {trackingNumber}
                </span>
                <button
                  onClick={copyTrackingNumber}
                  className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  title="Kopiera"
                >
                  {copied ? (
                    <CheckCircle size={20} className="text-green-600" />
                  ) : (
                    <Copy size={20} className="text-slate-500" />
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Spara detta nummer för att kunna följa upp ditt ärende
              </p>
            </div>
          )}
          
          <div className="space-y-3">
            <a
              href={`/track?number=${trackingNumber}`}
              className="block w-full bg-blue-600 text-white rounded-lg py-3 font-medium hover:bg-blue-700 transition-colors"
            >
              <MagnifyingGlass size={20} className="inline mr-2" />
              Spåra ditt ärende
            </a>
            <button
              onClick={handleNewReport}
              className="w-full bg-slate-100 text-slate-700 rounded-lg py-3 font-medium hover:bg-slate-200 transition-colors"
            >
              Rapportera nytt ärende
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <House size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">
                {organizationName || 'Felanmälan'}
              </h1>
              <p className="text-sm text-slate-500">Rapportera ett fel eller problem</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <WarningCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Contact info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Dina uppgifter</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Namn *
                </label>
                <input
                  type="text"
                  required
                  value={formData.reporter_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, reporter_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ditt namn"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  E-post *
                </label>
                <input
                  type="email"
                  required
                  value={formData.reporter_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, reporter_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="din@email.se"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.reporter_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, reporter_phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="070-123 45 67"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Lägenhetsnummer
                </label>
                <input
                  type="text"
                  value={formData.apartment_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, apartment_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. 1201"
                />
              </div>
            </div>
          </div>

          {/* Issue details */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Beskrivning av problemet</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Titel *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Kort beskrivning av problemet"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kategori
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.category === cat.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-xl block mb-1">{cat.icon}</span>
                      <span className="text-xs font-medium">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Detaljerad beskrivning
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Beskriv problemet mer detaljerat..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Hur brådskande är det?
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 1, label: 'Akut', color: 'red' },
                    { value: 2, label: 'Hög', color: 'orange' },
                    { value: 3, label: 'Normal', color: 'yellow' },
                    { value: 4, label: 'Låg', color: 'green' },
                    { value: 5, label: 'Planerad', color: 'slate' }
                  ].map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, urgency: level.value }))}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                        formData.urgency === level.value
                          ? `border-${level.color}-500 bg-${level.color}-50 text-${level.color}-700`
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                      style={{
                        borderColor: formData.urgency === level.value ? 
                          (level.color === 'red' ? '#ef4444' : 
                           level.color === 'orange' ? '#f97316' : 
                           level.color === 'yellow' ? '#eab308' : 
                           level.color === 'green' ? '#22c55e' : '#64748b') : undefined,
                        backgroundColor: formData.urgency === level.value ?
                          (level.color === 'red' ? '#fef2f2' : 
                           level.color === 'orange' ? '#fff7ed' : 
                           level.color === 'yellow' ? '#fefce8' : 
                           level.color === 'green' ? '#f0fdf4' : '#f8fafc') : undefined
                      }}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* File upload */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Bifoga bilder (valfritt)</h2>
            
            <div className="space-y-4">
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {files.map((file, index) => (
                    <div 
                      key={`file-${file.name}-${file.size}`}
                      className="relative group"
                    >
                      <div className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden">
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {files.length < 5 && (
                <label className="block">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <Camera size={32} className="text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      Klicka för att lägga till bilder
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Max 5 bilder, JPG eller PNG
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white rounded-xl py-4 font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Spinner size={20} className="animate-spin" />
                Skickar...
              </>
            ) : (
              <>
                <PaperPlaneTilt size={20} />
                Skicka rapport
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Förvaltningen
        </p>
      </div>
    </div>
  );
}
